/**
 * Redux-backed hook for the Product Catalog Control Center AI chatbot (Brisque AI).
 *
 * Replaces the old local mock engine (`useBrisqueAssistant`) — it now reads the
 * `ProductCatalogChatbot` slice and dispatches the real backend request actions
 * (§4 of the integration guide). The caller identity (§1) is assembled here from
 * the logged-in user + selected location and attached to every request.
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useAuth } from '../../../context/AuthContext';
import { useBranch } from '../../../context/BranchContext';
import { getUserId } from '../../../utils/hmac-utils';
import type {
    PCChatbotIdentity,
    PCMessage,
    PCDuration,
    PCSyncResultRow,
} from '../../../interface/ProductCatalogChatbotInterface';
import {
    createConversationRequest,
    listConversationsRequest,
    loadConversationRequest,
    sendMessageRequest,
    appendLocalMessage,
    renameConversationRequest,
    deleteConversationRequest,
    confirmCardRequest,
    cancelCardRequest,
    pickCandidateRequest,
    setCardChannelsRequest,
    setCardDurationRequest,
    removeCardRowRequest,
    fetchChannelsRequest,
    fetchCtasRequest,
    runSyncRequest,
    syncResultsRequest,
    syncRetryRequest,
    fetchLogRequest,
    undoLogEntryRequest,
    refreshCatalogueRequest,
    resetChatbot,
    clearUndoResult,
    clearUndoNav,
} from '../../../redux/productCatalog/ProductCatalogChatbot/ProductCatalogChatbotActions';

export function usePCChatbot() {
    const dispatch = useAppDispatch();
    const state = useAppSelector((s) => s.ProductCatalogChatbot);
    const { auth } = useAuth();
    const { selectedBranch } = useBranch();

    const identity: PCChatbotIdentity = useMemo(
        () => ({
            locationId:
                (selectedBranch as any)?.id ??
                (selectedBranch as any)?.locationId ??
                (auth as any)?.locationId ??
                (auth as any)?.location_id ??
                '',
            // The Control Centre user id (§1) — `id` is the real Control Centre staff UUID
            // and MUST be checked before `userId`, which on this login response is the
            // human-readable username (e.g. "manager@a2bprinceton"), not an id. Matches the
            // order proven correct by `getUserId()` in `utils/hmac-utils.ts` (used by the
            // real, working `items/update` write) — confirmed by a live debug capture where
            // `auth.userId` was the username and `auth.id` was the real UUID Control Centre
            // accepted. `AvailabilityControlCentre.tsx`'s OWN inline resolvers check `userId`
            // first (same bug, unconfirmed whether it's actually hit on those write paths).
            // `getUserId()` (the persisted CREDENTIALS blob) is consulted FIRST because this id
            // is the KEY the backend scopes chat history and the activity log by. The redux
            // `auth` object is populated asynchronously and its shape varies by login response,
            // so the same person could be seen as `<uuid>` on one render and
            // `manager@somewhere` (or, with no id at all, the backend's fallback bucket) on
            // another — and every earlier chat and log entry written under the previous value
            // silently vanished from the panels. localStorage is stable across reloads and is
            // already resolved before redux auth settles, so it keeps one person on one key.
            ccUserId: String(
                getUserId() ?? auth?.id ?? auth?.userId ?? auth?.staff_id ?? auth?.authUserId ?? ''
            ).trim(),
            token: auth?.accessToken ?? auth?.access_token ?? null,
        }),
        [selectedBranch, auth]
    );

    /** Map a Control Centre channel id → human name using the fetched channels list (§4.8). */
    const channelName = useCallback(
        (id: string): string => state.channels.find((c) => c.id === id)?.name ?? id,
        [state.channels]
    );

    const ready = !!identity.locationId && !!identity.ccUserId;

    // The active thread belongs to one location's Control Centre context — a stale
    // conversationId carried into a different location produces a backend duplicate-key
    // error (the id is unique per location) the moment a message is sent. Wipe the slice
    // back to initialState whenever the selected location actually changes.
    //
    // The "previous location" is read from REDUX, not from a component ref. The chatbot is
    // conditionally rendered (App.tsx), so navigating away from Product Catalog unmounts it and
    // destroys any ref: switching outlets while it was unmounted then looked like a first mount
    // on return (prev === null), the reset never fired, and the panel kept showing the PREVIOUS
    // outlet's thread — sending into it produced a stray standalone reply. Only a manual page
    // refresh cleared it, because that wipes the store too. Redux survives the unmount, so the
    // mismatch is still visible on remount.
    useEffect(() => {
        if (!identity.locationId) return;
        if (state.locationId === identity.locationId) return;
        dispatch(resetChatbot(identity.locationId));
    }, [identity.locationId, state.locationId, dispatch]);

    return {
        state,
        identity,
        ready,
        channelName,

        // Conversations (§4.1–4.6)
        createConversation: (fresh?: boolean, title?: string) =>
            dispatch(createConversationRequest({ identity, fresh, title })),
        listConversations: () => dispatch(listConversationsRequest({ identity })),
        /** Next page of older threads — `before` is the oldest one already listed. */
        loadOlderConversations: (before: string) => dispatch(listConversationsRequest({ identity, before })),
        loadConversation: (conversationId: string) =>
            dispatch(loadConversationRequest({ identity, conversationId })),
        sendMessage: (conversationId: string, text: string) =>
            dispatch(sendMessageRequest({ identity, conversationId, text })),
        appendLocalMessage: (message: PCMessage) => dispatch(appendLocalMessage(message)),
        rename: (conversationId: string, title: string) =>
            dispatch(renameConversationRequest({ identity, conversationId, title })),
        deleteThread: (conversationId: string) =>
            dispatch(deleteConversationRequest({ identity, conversationId })),

        // Action cards (§4.7 / §6)
        confirm: (cardId: string) => dispatch(confirmCardRequest({ identity, cardId })),
        cancelCard: (cardId: string) => dispatch(cancelCardRequest({ identity, cardId })),
        pickCandidate: (cardId: string, candidateId: string) =>
            dispatch(pickCandidateRequest({ identity, cardId, candidateId })),
        // Multi-select picker: every ticked candidate resolved in one go.
        pickCandidates: (cardId: string, candidateIds: string[]) =>
            dispatch(pickCandidateRequest({ identity, cardId, candidateId: candidateIds })),
        setRowChannels: (cardId: string, rowId: string, channels: string[]) =>
            dispatch(setCardChannelsRequest({ identity, cardId, rowId, channels })),
        setRowDuration: (cardId: string, rowId: string, duration: PCDuration) =>
            dispatch(setCardDurationRequest({ identity, cardId, rowId, duration })),
        removeRow: (cardId: string, rowId: string) =>
            dispatch(removeCardRowRequest({ identity, cardId, rowId })),

        // Channels (§4.8)
        fetchChannels: () => dispatch(fetchChannelsRequest({ identity })),

        // Quick-action CTAs (§Item 6)
        fetchCtas: () => dispatch(fetchCtasRequest({ identity })),

        // Sync (§4.9 / §7)
        runSync: (conversationId: string, msgId: string) =>
            dispatch(runSyncRequest({ identity, conversationId, msgId })),
        fetchSyncResults: (conversationId: string, msgId: string) =>
            dispatch(syncResultsRequest({ identity, conversationId, msgId })),
        retrySync: (conversationId: string, msgId: string, previous: PCSyncResultRow[]) =>
            dispatch(syncRetryRequest({ identity, conversationId, msgId, previous })),

        // Activity log & undo (§4.10 / §8)
        fetchLog: () => dispatch(fetchLogRequest({ identity })),
        /** Next page of older activity — `before` is the oldest entry already listed. */
        loadOlderLog: (before: string) => dispatch(fetchLogRequest({ identity, before })),
        // The saga resolves which thread the reversal is written into (the open one, else the
        // newest), so callers never have to pass it.
        undo: (entryId: string) => dispatch(undoLogEntryRequest({ identity, entryId })),
        clearUndoResult: () => dispatch(clearUndoResult()),
        clearUndoNav: () => dispatch(clearUndoNav()),

        // Manual refresh (§4.11)
        refreshCatalogue: () => dispatch(refreshCatalogueRequest({ identity })),

        // Local
        reset: () => dispatch(resetChatbot()),
    };
}

export type PCChatbot = ReturnType<typeof usePCChatbot>;
